import React from 'react';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import TextureOverlay from '../components/team/TextureOverlay';
import SiteHeader from '../components/team/SiteHeader';
import ContactBlock from '../components/team/ContactBlock';
import Footer from '../components/team/Footer';

const TITLE = 'Contact Mubarak Farm | Uganda Robusta Supplier';
const DESCRIPTION = 'Contact Mubarak Farm about Uganda Robusta green beans, wholesale pricing, samples, or a farm visit in Luweero. Email, WhatsApp, or send a message.';
const OG_IMAGE = 'https://mubarakfarmug.com/flow-assets/content/og-mubarak-farm.jpg';

const meta = {
  title: TITLE,
  meta: [
    { name: 'description', content: DESCRIPTION },
    { name: 'robots', content: 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1' },
    { property: 'og:title', content: TITLE },
    { property: 'og:description', content: DESCRIPTION },
    { property: 'og:type', content: 'website' },
    { property: 'og:url', content: 'https://mubarakfarmug.com/contact' },
    { property: 'og:image', content: OG_IMAGE },
    { property: 'og:image:width', content: '1200' },
    { property: 'og:image:height', content: '630' },
    { property: 'og:image:type', content: 'image/jpeg' },
    { property: 'og:image:alt', content: 'Ripening coffee cherries on a branch' },
    { property: 'og:site_name', content: 'Mubarak Farm Uganda' },
    { name: 'twitter:card', content: 'summary_large_image' },
    { name: 'twitter:title', content: TITLE },
    { name: 'twitter:description', content: DESCRIPTION },
    { name: 'twitter:image', content: OG_IMAGE },
  ],
  link: [
    { rel: 'canonical', href: 'https://mubarakfarmug.com/contact' },
  ],
  script: [
    {
      type: 'application/ld+json',
      innerHTML: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'ContactPage',
        '@id': 'https://mubarakfarmug.com/contact#webpage',
        'url': 'https://mubarakfarmug.com/contact',
        'name': 'Contact Mubarak Farm',
        'description': DESCRIPTION,
        'inLanguage': 'en',
        'isPartOf': { '@type': 'WebSite', '@id': 'https://mubarakfarmug.com/#website', 'name': 'Mubarak Farm', 'url': 'https://mubarakfarmug.com/' },
        'about': { '@type': 'Organization', '@id': 'https://mubarakfarmug.com/#organization', 'name': 'Mubarak Farm', 'url': 'https://mubarakfarmug.com/' }
      })
    },
    {
      type: 'application/ld+json',
      innerHTML: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        'itemListElement': [
          { '@type': 'ListItem', 'position': 1, 'name': 'Home', 'item': 'https://mubarakfarmug.com/' },
          { '@type': 'ListItem', 'position': 2, 'name': 'Contact', 'item': 'https://mubarakfarmug.com/contact' }
        ]
      })
    }
  ]
};

export default function Contact() {
  return (
    <React.Fragment>
      <HelmetProvider>
        <Helmet {...meta}></Helmet>
      </HelmetProvider>
      <TextureOverlay fixed />
      <SiteHeader current="/contact" />
      <main>
        <section className="relative pt-36 pb-20 lg:pt-44 lg:pb-28">
          <div className="container mx-auto px-4 lg:px-8 relative z-10">
            <div className="max-w-6xl mx-auto">
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 lg:items-end mb-12 lg:mb-16">
                <div className="lg:col-span-7">
                  <nav aria-label="Breadcrumb" className="mb-8">
                    <ol className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-gray-500">
                      <li><a href="/" className="hover:text-clay transition-colors">Home</a></li>
                      <li aria-hidden="true">/</li>
                      <li aria-current="page" className="text-gray-900 font-semibold">Contact</li>
                    </ol>
                  </nav>

                  <p className="text-xs font-semibold uppercase tracking-[0.2em] text-clay mb-4">Contact</p>
                  <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl leading-[1.05] tracking-tight text-gray-900 mb-6 max-w-4xl">
                    Talk to <span className="text-clay">Mubarak Farm</span>.
                  </h1>
                  <p className="text-lg text-gray-700 max-w-2xl leading-relaxed">
                    Questions about green bean grades, wholesale pricing, samples, or a visit to the farm in Luweero. Send a message, or reach the team directly on WhatsApp.
                  </p>
                </div>

                <figure className="lg:col-span-5">
                  <img
                    className="w-full aspect-[16/9] lg:aspect-[5/4] object-cover rounded-2xl"
                    src="/flow-assets/content/farm/cherries-in-basin-600.webp"
                    srcSet="/flow-assets/content/farm/cherries-in-basin-600.webp 600w, /flow-assets/content/farm/cherries-in-basin-1200.webp 1200w"
                    sizes="(min-width: 1024px) 40vw, 100vw"
                    width="1200"
                    height="2133"
                    alt="A hand resting in a basin full of freshly picked red and yellow coffee cherries"
                  />
                </figure>
              </div>

              <ContactBlock formHeading="h2" />
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </React.Fragment>
  );
}
